import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ArrowLeft, Plus, Trash2, RefreshCw, AlertTriangle, Eye } from "lucide-react";

const CATEGORIES = [
  { value: "limpieza_banos", label: "Banos" },
  { value: "limpieza_cocina", label: "Cocina" },
  { value: "limpieza_comedor", label: "Comedor" },
  { value: "limpieza_general", label: "General" },
];

function emptyTask() {
  return {
    _tempId: Math.random().toString(36).slice(2),
    title: "",
    category: "limpieza_general",
    scheduled_hour: "08:00",
    interval_hours: 0,
    until_hour: "08:00",
    responsible_position: "EG01",
    photo_required: false,
    estimated_minutes: 15,
    due_window_minutes: 30,
  };
}

function expandPreview(task) {
  const times = [];
  if (!task.scheduled_hour) return times;
  const [sh, sm] = task.scheduled_hour.split(":").map(Number);
  const until = task.until_hour || task.scheduled_hour;
  const [uh, um] = until.split(":").map(Number);
  const interval = parseInt(task.interval_hours || 0, 10);

  let t = sh * 60 + sm;
  const end = uh * 60 + um;
  while (t <= end) {
    const h = String(Math.floor(t / 60)).padStart(2, "0");
    const m = String(t % 60).padStart(2, "0");
    times.push(`${h}:${m}`);
    if (interval > 0) t += interval * 60;
    else break;
  }
  return times;
}

export default function OpsCleaningRotationForm() {
  const { id } = useParams(); // undefined = nueva
  const { user } = useAuth();
  const navigate = useNavigate();
  const isNew = !id || id === "nueva";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [branchId, setBranchId] = useState("all");
  const [active, setActive] = useState(true);
  const [tasks, setTasks] = useState([emptyTask()]);
  const [branches, setBranches] = useState([]);
  const [positionCodes, setPositionCodes] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    api.get("/branches").then(r => setBranches(r.data.filter(b => !b.is_corporate))).catch(() => {});
    api.get("/ops/position-codes").then(r => setPositionCodes(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await api.get(`/ops/cleaning/rotations/${id}`);
      const r = res.data;
      setName(r.name || "");
      setDescription(r.description || "");
      setBranchId(r.branch_id || "all");
      setActive(r.active !== false);
      setTasks((r.tasks || []).map(t => ({ ...t, _tempId: t.id })));
    } catch {
      setError("No se pudo cargar la rotacion.");
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const addTask = () => setTasks(prev => [...prev, emptyTask()]);

  const removeTask = (idx) => setTasks(prev => prev.filter((_, i) => i !== idx));

  const updateTask = (idx, field, value) => {
    setTasks(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const handleSave = async () => {
    setError("");
    if (!name.trim()) { setError("El nombre es obligatorio."); return; }
    if (tasks.length === 0) { setError("Agrega al menos una tarea."); return; }
    for (const t of tasks) {
      if (!t.title.trim()) { setError("Cada tarea debe tener un titulo."); return; }
    }

    const payload = {
      name: name.trim(),
      description: description.trim(),
      branch_id: branchId || "all",
      active,
      tasks: tasks.map(t => ({
        id: !t._tempId || t._tempId === t.id ? t._tempId : undefined,
        title: t.title,
        category: t.category,
        scheduled_hour: t.scheduled_hour,
        interval_hours: parseInt(t.interval_hours || 0, 10),
        until_hour: t.until_hour || t.scheduled_hour,
        responsible_position: t.responsible_position,
        photo_required: !!t.photo_required,
        estimated_minutes: parseInt(t.estimated_minutes || 15, 10),
        due_window_minutes: parseInt(t.due_window_minutes || 30, 10),
      })),
    };

    setSaving(true);
    try {
      if (isNew) {
        await api.post("/ops/cleaning/rotations", payload);
      } else {
        await api.put(`/ops/cleaning/rotations/${id}`, payload);
      }
      navigate("/ops/admin/cleaning-rotations");
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-300" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl">
      <button
        data-testid="back-rotation-btn"
        onClick={() => navigate("/ops/admin/cleaning-rotations")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a rotaciones
      </button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {isNew ? "Nueva rotacion" : "Editar rotacion"}
        </h1>
        <p className="text-sm text-slate-500">Configura las tareas y horarios</p>
      </div>

      {error && (
        <div data-testid="form-error" className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Datos generales */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="font-semibold text-slate-900 text-sm">Datos generales</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Nombre *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              data-testid="rotation-name-input"
              placeholder="Ej: Rotacion estandar de limpieza"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Sucursal aplicable</label>
            <select
              value={branchId}
              onChange={e => setBranchId(e.target.value)}
              data-testid="rotation-branch-input"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            >
              <option value="all">Todas las sucursales</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Descripcion (opcional)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              data-testid="rotation-description-input"
              rows={2}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-slate-500">Activa</label>
            <button
              type="button"
              data-testid="rotation-active-toggle"
              onClick={() => setActive(v => !v)}
              className={`relative w-10 h-5 rounded-full transition-colors ${active ? "bg-green-500" : "bg-slate-300"}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${active ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
            <span className="text-sm text-slate-600 font-medium">{active ? "Activa" : "Inactiva"}</span>
          </div>
        </div>
      </div>

      {/* Tareas */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900 text-sm">Tareas ({tasks.length})</h2>
          <div className="flex gap-2">
            <button
              data-testid="toggle-preview-btn"
              onClick={() => setShowPreview(v => !v)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              <Eye className="w-3.5 h-3.5" />
              {showPreview ? "Ocultar preview" : "Ver preview del dia"}
            </button>
            <button
              data-testid="add-task-btn"
              onClick={addTask}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#FFDF00] text-slate-900 rounded-lg font-semibold hover:bg-yellow-400"
            >
              <Plus className="w-3.5 h-3.5" />
              Agregar tarea
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {tasks.map((task, idx) => (
            <div
              key={task._tempId || idx}
              data-testid={`task-row-${idx}`}
              className="border border-slate-200 rounded-lg p-4 space-y-3 relative"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-xs font-semibold text-slate-400">Tarea {idx + 1}</span>
                {tasks.length > 1 && (
                  <button
                    data-testid={`remove-task-btn-${idx}`}
                    onClick={() => removeTask(idx)}
                    className="p-1 text-red-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Titulo *</label>
                  <input
                    type="text"
                    value={task.title}
                    onChange={e => updateTask(idx, "title", e.target.value)}
                    data-testid={`task-title-${idx}`}
                    placeholder="Ej: Limpieza de banos"
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  />
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Categoria</label>
                  <select
                    value={task.category}
                    onChange={e => updateTask(idx, "category", e.target.value)}
                    data-testid={`task-category-${idx}`}
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  >
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Puesto responsable</label>
                  <select
                    value={task.responsible_position}
                    onChange={e => updateTask(idx, "responsible_position", e.target.value)}
                    data-testid={`task-position-${idx}`}
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  >
                    {positionCodes.length > 0
                      ? positionCodes.map(p => <option key={p.code} value={p.code}>{p.code} - {p.label}</option>)
                      : ["GTE00","SG01","ENT01","CJA01","COC01","EG01"].map(c => <option key={c} value={c}>{c}</option>)
                    }
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Hora de inicio</label>
                  <input
                    type="time"
                    value={task.scheduled_hour}
                    onChange={e => updateTask(idx, "scheduled_hour", e.target.value)}
                    data-testid={`task-scheduled-hour-${idx}`}
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  />
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Repetir cada (horas, 0 = una vez)</label>
                  <input
                    type="number"
                    min={0}
                    max={12}
                    value={task.interval_hours}
                    onChange={e => updateTask(idx, "interval_hours", e.target.value)}
                    data-testid={`task-interval-${idx}`}
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  />
                </div>

                {parseInt(task.interval_hours || 0, 10) > 0 && (
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Hasta la hora</label>
                    <input
                      type="time"
                      value={task.until_hour || task.scheduled_hour}
                      onChange={e => updateTask(idx, "until_hour", e.target.value)}
                      data-testid={`task-until-hour-${idx}`}
                      className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Duracion estimada (min)</label>
                  <input
                    type="number"
                    min={1}
                    value={task.estimated_minutes}
                    onChange={e => updateTask(idx, "estimated_minutes", e.target.value)}
                    data-testid={`task-estimated-${idx}`}
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  />
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Ventana limite (min)</label>
                  <input
                    type="number"
                    min={5}
                    value={task.due_window_minutes}
                    onChange={e => updateTask(idx, "due_window_minutes", e.target.value)}
                    data-testid={`task-due-window-${idx}`}
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <label className="text-xs text-slate-500">Foto obligatoria</label>
                  <button
                    type="button"
                    data-testid={`task-photo-toggle-${idx}`}
                    onClick={() => updateTask(idx, "photo_required", !task.photo_required)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${task.photo_required ? "bg-green-500" : "bg-slate-300"}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${task.photo_required ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                </div>
              </div>

              {/* Preview inline de horarios */}
              {showPreview && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-400 mb-1">Horarios generados para un dia tipico:</p>
                  <div className="flex flex-wrap gap-1">
                    {expandPreview(task).map((t, ti) => (
                      <span key={ti} className="text-xs bg-[#FFDF00] text-slate-800 rounded px-1.5 py-0.5 font-medium">
                        {t}
                      </span>
                    ))}
                    {expandPreview(task).length === 0 && (
                      <span className="text-xs text-slate-400">— sin horarios calculables</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Botones guardar */}
      <div className="flex gap-3">
        <button
          data-testid="cancel-rotation-btn"
          onClick={() => navigate("/ops/admin/cleaning-rotations")}
          className="px-5 py-2.5 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
        >
          Cancelar
        </button>
        <button
          data-testid="save-rotation-btn"
          onClick={handleSave}
          disabled={saving}
          className="flex-1 md:flex-none px-6 py-2.5 bg-[#FFDF00] text-slate-900 rounded-lg font-bold text-sm hover:bg-yellow-400 disabled:opacity-50"
        >
          {saving ? "Guardando..." : isNew ? "Crear rotacion" : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}
